import { motion } from "framer-motion";
import { useEffect, useContext } from "react";
import { LanguageContext } from "../../../context/LanguageContext";

const container = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.08 } },
};

const sectionFade = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } },
};

export default function CookiePolicy() {
  const { t, isRTL } = useContext(LanguageContext);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const types = t("cookiePolicy.types.sections", { returnObjects: true });

  return (
    <section
      dir={isRTL ? "rtl" : "ltr"}
      className="relative bg-gradient-to-b from-rose-50/50 via-white to-white py-24"
    >
      <div className="mx-auto max-w-4xl px-6">

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-20 text-center"
        >
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900">
            {t("cookiePolicy.header.title")}
          </h1>
          <p className="mt-4 text-gray-600">
            {t("cookiePolicy.header.updated")}
          </p>
        </motion.div>

        {/* Content */}
        <motion.div
          variants={container}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="space-y-10"
        >
          {/* Introduction */}
          <motion.div variants={sectionFade} className="legal-card">
            <h2 className="legal-title">
              {t("cookiePolicy.intro.title")}
            </h2>
            <p
              dangerouslySetInnerHTML={{
                __html: t("cookiePolicy.intro.text"),
              }}
            />
          </motion.div>

          {/* What are cookies */}
          <motion.div variants={sectionFade} className="legal-card">
            <h2 className="legal-title">
              {t("cookiePolicy.whatAreCookies.title")}
            </h2>
            <p>{t("cookiePolicy.whatAreCookies.text")}</p>
          </motion.div>

          {/* Types */}
          <motion.div variants={sectionFade} className="legal-card">
            <h2 className="legal-title">
              {t("cookiePolicy.types.title")}
            </h2>

            {Object.values(types).map((section, i) => (
              <div key={i} className="mb-6">
                <p className="font-semibold mb-2">{section.title}</p>
                <ul className="list-disc pl-6 space-y-2">
                  {section.items.map((item, idx) => (
                    <li key={idx}>{item}</li>
                  ))}
                </ul>
              </div>
            ))}
          </motion.div>

          {/* Usage */}
          <motion.div variants={sectionFade} className="legal-card">
            <h2 className="legal-title">
              {t("cookiePolicy.usage.title")}
            </h2>
            <ul className="list-disc pl-6 space-y-2">
              {t("cookiePolicy.usage.items", { returnObjects: true }).map(
                (item, i) => (
                  <li key={i}>{item}</li>
                )
              )}
            </ul>
          </motion.div>

          {/* Choices */}
          <motion.div variants={sectionFade} className="legal-card">
            <h2 className="legal-title">
              {t("cookiePolicy.choices.title")}
            </h2>
            <ul className="list-disc pl-6 space-y-2">
              {t("cookiePolicy.choices.items", { returnObjects: true }).map(
                (item, i) => (
                  <li key={i}>{item}</li>
                )
              )}
            </ul>

            <div className="mt-4 rounded-xl border bg-gray-50 p-4 text-sm">
              {t("cookiePolicy.choices.browsers")}
            </div>
          </motion.div>

          {/* Consent */}
          <motion.div variants={sectionFade} className="legal-card">
            <h2 className="legal-title">
              {t("cookiePolicy.consent.title")}
            </h2>
            <p>{t("cookiePolicy.consent.text")}</p>
          </motion.div>

          {/* Contact */}
          <motion.div variants={sectionFade} className="legal-card">
            <h2 className="legal-title">
              {t("cookiePolicy.contact.title")}
            </h2>
            <p>{t("cookiePolicy.contact.text")}</p>
            <p className="mt-2">
              <a
                href={`mailto:${t("cookiePolicy.contact.email")}`}
                className="text-rose-600 hover:underline"
              >
                {t("cookiePolicy.contact.email")}
              </a>
              <br />
              <a
                href={t("cookiePolicy.contact.website")}
                target="_blank"
                rel="noreferrer"
                className="text-rose-600 hover:underline"
              >
                {t("cookiePolicy.contact.website")}
              </a>
            </p>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
